import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, notification, Timeline, Spin, Calendar, Modal, Form, Select, InputNumber, Switch, Progress } from 'antd';
import { 
  ClockCircleOutlined as ClockIcon, 
  ReloadOutlined as RefreshCwIcon,
  PlayCircleOutlined as PlayIcon,
  PauseCircleOutlined as PauseIcon,
  SettingOutlined as SettingsIcon,
  CalendarOutlined as CalendarIcon,
  RiseOutlined as TrendingUpIcon,
  CheckCircleOutlined as CheckCircleIcon,
  ExclamationCircleOutlined as AlertTriangleIcon
} from '@ant-design/icons';
import { aiService } from '../../services/ai';
import type { Dayjs } from 'dayjs';

const { Option } = Select;

interface TrainingSchedule {
  id: string;
  schedule_name: string;
  schedule_type: 'hourly' | 'daily' | 'weekly' | 'monthly';
  model_type: string;
  is_active: boolean;
  last_run: string;
  next_run: string;
  success_rate: number;
  avg_duration: number; // in minutes
  total_runs: number;
  configuration: any;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

interface TrainingSession {
  id: string;
  session_name: string;
  session_type: string;
  model_type: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at?: string;
  duration: number; // in seconds
  accuracy_improvement: number;
  error_message?: string;
}

const ScheduledTrainingInterface: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState<any>(null);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [form] = Form.useForm();

  // Mock scheduled training data
  const [schedules] = useState<TrainingSchedule[]>([
    {
      id: '1',
      schedule_name: 'Hourly External Data Sync',
      schedule_type: 'hourly',
      model_type: 'threat_detection',
      is_active: true,
      last_run: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
      next_run: new Date(Date.now() + 1000 * 60 * 15).toISOString(),
      success_rate: 98.5,
      avg_duration: 12,
      total_runs: 2847,
      configuration: { learning_rate: 0.001, batch_size: 32 },
      priority: 'high'
    },
    {
      id: '2',
      schedule_name: 'Weekly Full Model Training',
      schedule_type: 'weekly',
      model_type: 'behavioral_analysis',
      is_active: true,
      last_run: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
      next_run: new Date(Date.now() + 1000 * 60 * 60 * 24 * 5).toISOString(),
      success_rate: 94.2,
      avg_duration: 180,
      total_runs: 52,
      configuration: { epochs: 10, validation_split: 0.2 },
      priority: 'critical'
    },
    {
      id: '3',
      schedule_name: 'Monthly Performance Review',
      schedule_type: 'monthly',
      model_type: 'pattern_recognition',
      is_active: true,
      last_run: new Date(Date.now() - 1000 * 60 * 60 * 24 * 15).toISOString(),
      next_run: new Date(Date.now() + 1000 * 60 * 60 * 24 * 15).toISOString(),
      success_rate: 96.7,
      avg_duration: 420,
      total_runs: 12,
      configuration: { full_retrain: true, performance_threshold: 0.85 },
      priority: 'medium'
    },
    {
      id: '4',
      schedule_name: 'Daily Incremental Learning',
      schedule_type: 'daily',
      model_type: 'threat_detection',
      is_active: false,
      last_run: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
      next_run: new Date(Date.now() + 1000 * 60 * 60 * 12).toISOString(),
      success_rate: 91.3,
      avg_duration: 45,
      total_runs: 180,
      configuration: { incremental_only: true },
      priority: 'low'
    }
  ]);

  // Mock recent training sessions
  const [sessions] = useState<TrainingSession[]>([
    {
      id: '1',
      session_name: 'Threat Detection Update',
      session_type: 'incremental',
      model_type: 'threat_detection',
      status: 'completed',
      started_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
      completed_at: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
      duration: 900, // 15 minutes
      accuracy_improvement: 2.3
    },
    {
      id: '2',
      session_name: 'Behavioral Analysis Training',
      session_type: 'scheduled',
      model_type: 'behavioral_analysis',
      status: 'running',
      started_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
      duration: 1800, // 30 minutes so far
      accuracy_improvement: 0
    },
    {
      id: '3',
      session_name: 'Pattern Recognition Optimization',
      session_type: 'manual',
      model_type: 'pattern_recognition',
      status: 'failed',
      started_at: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
      completed_at: new Date(Date.now() - 1000 * 60 * 110).toISOString(),
      duration: 600, // 10 minutes
      accuracy_improvement: 0,
      error_message: 'Insufficient training data'
    }
  ]);

  const fetchTrainingData = async () => {
    try {
      setRefreshing(true);
      const status = await aiService.getTrainingStatus();
      setTrainingStatus(status);
      
      notification.success({
        message: '📊 Scheduled Training Verileri Güncellendi',
        description: 'Training schedule durumu yüklendi.',
        placement: 'topRight'
      });
    } catch (error) {
      console.error('Training data fetch error:', error);
      notification.error({
        message: '❌ Veri Yüklenemedi',
        description: 'Scheduled training verileri alınamadı.',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const triggerManualTraining = async (values: any) => {
    try {
      setTriggering(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      notification.success({
        message: '🚀 Manual Training Başlatıldı',
        description: 'Training session queue\'ya eklendi ve yakında başlayacak.',
        placement: 'topRight'
      });
      
      setScheduleModal(false);
      form.resetFields();
      fetchTrainingData();
      
    } catch (error) {
      console.error('Manual training trigger error:', error);
      notification.error({
        message: '❌ Training Başlatılamadı',
        description: 'Manual training tetiklenirken hata oluştu.',
        placement: 'topRight'
      });
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    fetchTrainingData();
    
    // Auto refresh every minute
    const interval = setInterval(fetchTrainingData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getScheduleStatusColor = (isActive: boolean) => isActive ? 'green' : 'red';
  const getSessionStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'running': return 'blue';
      case 'queued': return 'orange';
      case 'failed': return 'red';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getScheduleTypeText = (type: string) => {
    switch (type) {
      case 'hourly': return 'Saatlik';
      case 'daily': return 'Günlük';
      case 'weekly': return 'Haftalık';
      case 'monthly': return 'Aylık';
      default: return type;
    }
  };

  const scheduleColumns = [
    {
      title: 'Schedule',
      dataIndex: 'schedule_name',
      key: 'schedule_name',
      render: (text: string, record: TrainingSchedule) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.model_type} • {getScheduleTypeText(record.schedule_type)}
          </div>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={getScheduleStatusColor(isActive)}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>
          {priority.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Başarı Oranı',
      dataIndex: 'success_rate',
      key: 'success_rate',
      render: (rate: number) => (
        <Progress 
          percent={rate} 
          size="small"
          strokeColor={rate > 95 ? '#52c41a' : rate > 90 ? '#fa8c16' : '#ff4d4f'}
        />
      ),
    },
    {
      title: 'Sonraki Çalışma',
      dataIndex: 'next_run',
      key: 'next_run',
      render: (date: string) => {
        const timeLeft = new Date(date).getTime() - Date.now();
        const hoursLeft = Math.round(timeLeft / (1000 * 60 * 60));
        return (
          <div style={{ fontSize: '12px' }}>
            <div>{new Date(date).toLocaleString('tr-TR')}</div>
            <div style={{ color: hoursLeft < 1 ? '#ff4d4f' : '#666' }}>
              {hoursLeft < 1 ? `${Math.round(timeLeft / (1000 * 60))} dakika kaldı` : `${hoursLeft} saat kaldı`}
            </div>
          </div>
        );
      },
    },
  ];

  const sessionColumns = [
    {
      title: 'Session',
      dataIndex: 'session_name',
      key: 'session_name',
      render: (text: string, record: TrainingSession) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.model_type} • {record.session_type}
          </div>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getSessionStatusColor(status)}>
          {status === 'completed' && 'Tamamlandı'}
          {status === 'running' && 'Çalışıyor'}
          {status === 'queued' && 'Sırada'}
          {status === 'failed' && 'Başarısız'}
        </Tag>
      ),
    },
    {
      title: 'Süre',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: number) => (
        <span>{Math.round(duration / 60)} dakika</span>
      ),
    },
    {
      title: 'Performans İyileştirmesi',
      dataIndex: 'accuracy_improvement',
      key: 'accuracy_improvement',
      render: (improvement: number, record: TrainingSession) => (
        <span style={{ 
          color: improvement > 0 ? '#52c41a' : record.status === 'failed' ? '#ff4d4f' : '#666',
          fontWeight: 600 
        }}>
          {record.status === 'running' ? 'Devam ediyor...' : 
           record.status === 'failed' ? 'Başarısız' :
           improvement > 0 ? `+${improvement.toFixed(1)}%` : 'Değişiklik yok'}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px', fontSize: '16px' }}>📊 Scheduled Training Verileri Yükleniyor...</p>
      </div>
    );
  }

  const activeSchedules = schedules.filter(s => s.is_active).length;
  const runningSessions = sessions.filter(s => s.status === 'running').length;
  const completedToday = sessions.filter(s => s.status === 'completed' && 
    new Date(s.completed_at!).toDateString() === new Date().toDateString()).length;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>
            📊 Scheduled Training Interface
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Otomatik training schedule'ları ve manuel training yönetimi
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            icon={<PlayIcon  />}
            onClick={() => setScheduleModal(true)}
            type="primary"
          >
            Manual Training
          </Button>
          <Button 
            icon={<RefreshCwIcon  />}
            onClick={fetchTrainingData}
            loading={refreshing}
          >
            Yenile
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aktif Schedule'lar"
              value={activeSchedules}
              suffix={`/ ${schedules.length}`}
              prefix={<ClockIcon  />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Çalışan Session'lar"
              value={runningSessions}
              prefix={<PlayIcon  />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bugün Tamamlanan"
              value={completedToday}
              prefix={<CheckCircleIcon  />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ortalama Başarı"
              value={Math.round(schedules.reduce((sum, s) => sum + s.success_rate, 0) / schedules.length)}
              suffix="%"
              prefix={<TrendingUpIcon  />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Training Schedules */}
        <Col xs={24} lg={14}>
          <Card 
            title="⏰ Training Schedule'ları"
            extra={<Tag color="blue">Otomatik</Tag>}
            style={{ marginBottom: '16px' }}
          >
            <Table
              dataSource={schedules}
              columns={scheduleColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>

          {/* Recent Sessions */}
          <Card 
            title="🔄 Son Training Session'ları"
            extra={<Tag color="green">Canlı</Tag>}
          >
            <Table
              dataSource={sessions}
              columns={sessionColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Training Calendar & Timeline */}
        <Col xs={24} lg={10}>
          <Card 
            title="📅 Training Takvimi"
            style={{ marginBottom: '16px', height: '350px' }}
          >
            <Calendar 
              fullscreen={false}
              onSelect={(date: Dayjs) => {
                console.log('Selected date:', date.format('YYYY-MM-DD'));
              }}
            />
          </Card>

          <Card 
            title="⚡ Training Activity"
            style={{ height: '350px', overflowY: 'auto' }}
          >
            <Timeline
              items={[
                {
                  color: 'green',
                  children: (
                    <div>
                      <strong>Hourly Sync Completed</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Threat detection model updated | 45 dakika önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'blue',
                  children: (
                    <div>
                      <strong>Behavioral Analysis Running</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Weekly training in progress | 30 dakika önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'red',
                  children: (
                    <div>
                      <strong>Pattern Recognition Failed</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Insufficient data - retrying in 1 hour | 2 saat önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'purple',
                  children: (
                    <div>
                      <strong>Manual Training Queued</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Custom optimization scheduled | 3 saat önce
                      </small>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Manual Training Modal */}
      <Modal
        title="🚀 Manual Training Başlat"
        open={scheduleModal}
        onCancel={() => setScheduleModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={triggerManualTraining}
        >
          <Form.Item
            name="model_type"
            label="Model Türü"
            rules={[{ required: true, message: 'Model türünü seçin' }]}
          >
            <Select placeholder="Hangi model train edilecek?">
              <Option value="threat_detection">🚨 Threat Detection</Option>
              <Option value="behavioral_analysis">👤 Behavioral Analysis</Option>
              <Option value="pattern_recognition">🔍 Pattern Recognition</Option>
              <Option value="all_models">🧠 Tüm Modeller</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="training_type"
            label="Training Türü"
            rules={[{ required: true, message: 'Training türünü seçin' }]}
          >
            <Select placeholder="Training türünü seçin">
              <Option value="incremental">⚡ Incremental Learning</Option>
              <Option value="full_retrain">🔄 Full Retrain</Option>
              <Option value="fine_tuning">🎯 Fine Tuning</Option>
              <Option value="evaluation">📊 Model Evaluation</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="learning_rate"
                label="Learning Rate"
                initialValue={0.001}
              >
                <InputNumber
                  min={0.0001}
                  max={0.1}
                  step={0.0001}
                  precision={4}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="batch_size"
                label="Batch Size"
                initialValue={32}
              >
                <InputNumber
                  min={8}
                  max={128}
                  step={8}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="priority"
            label="Öncelik"
            initialValue="medium"
          >
            <Select>
              <Option value="low">🟢 Düşük</Option>
              <Option value="medium">🟡 Orta</Option>
              <Option value="high">🟠 Yüksek</Option>
              <Option value="critical">🔴 Kritik</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="background_mode"
            label="Background Mode"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Arka planda" unCheckedChildren="Öncelikli" />
          </Form.Item>

          <Form.Item style={{ marginTop: '24px', textAlign: 'right' }}>
            <Button 
              style={{ marginRight: '8px' }}
              onClick={() => setScheduleModal(false)}
            >
              İptal
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={triggering}
              icon={<PlayIcon  />}
            >
              Training Başlat
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScheduledTrainingInterface;
